"use client"

import { useState, useEffect, useMemo } from "react"
import { Dropdown } from "react-bootstrap"
import { Accordion, Col } from "react-bootstrap"

//Images
import defaultAvatar from "../../../../../src/assets/images/placeholder.jpg"
import { useParams, useLocation } from "react-router-dom"
import { getPatientDetailsAction } from "../../../../store/actions/PatientsAction"
import { useDispatch, useSelector } from "react-redux"
import ImportantQuestion from "../Patients/Components/ImportentQuestion.Component"
import Loader from "../../Common/Loader.jsx"
import { getNotesAction } from "../../../../store/actions/NotesAction"
import { getCasePaperDataAction } from "../../../../store/actions/CasePaperAction"
import { formatDate } from "../../../../utils/dateUtils.js"
import { NotesHistory } from "../Patients/Components/NotesHistory.jsx"
import Header from "../../../layouts/nav/Header.jsx" // adjust path if needed
import NavHader from "../../../layouts/nav/NavHader"

const CasePaper = () => {
  const dispatch = useDispatch()
  const { patientId } = useParams()
  const location = useLocation()
  const patientData = location.state?.patientData

  const getPatientIdFromUrl = () => {
    if (patientId) {
      return patientId
    }
    const pathSegments = window.location.pathname.split("/")
    const lastSegment = pathSegments[pathSegments.length - 1]
    if (lastSegment && lastSegment !== "case-paper" && lastSegment !== "") {
      return lastSegment
    }
    try {
      const storedPatientData = sessionStorage.getItem("casePaperPatientData")
      if (storedPatientData) {
        const parsedData = JSON.parse(storedPatientData)
        return parsedData.patient_id
      }
    } catch (error) {
      console.error("Error parsing patient data from sessionStorage:", error)
    }
    return null
  }

  const currentPatientId = getPatientIdFromUrl()

  useEffect(() => {
    console.log("Current patient ID:", currentPatientId)
    console.log("URL params patientId:", patientId)
    console.log("Location pathname:", window.location.pathname)
  }, [currentPatientId, patientId])

  const casePaperData = useSelector((state) => {
    console.log("Case paper data:", state.casePaper)
    return state.casePaper?.casePaperData || null
  })

  const apiData = casePaperData?.casePaperData || casePaperData || null
  const safeApiData = apiData

  // --------------------------- SUMMARY SECTION LOGIC ---------------------------
  // 1️⃣ Extract first visit
  let firstVisitDate = null
  if (safeApiData?.timeline) {
    const allDates = Object.keys(safeApiData.timeline).sort() // ascending YYYY-MM-DD
    if (allDates.length > 0) {
      firstVisitDate = allDates[0]
    }
  }

  // fallback using lastVisits (if timeline missing)
  if (!firstVisitDate && safeApiData?.lastVisits?.date) {
    firstVisitDate = safeApiData.lastVisits.date
  }

  // 2️⃣ Calculate duration from first visit
  const calculateDuration = (dateStr) => {
    if (!dateStr) return "-"
    const start = new Date(dateStr)
    const end = new Date()
    const diffMs = end - start
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    if (diffDays < 30) return `${diffDays} days`
    const months = Math.floor(diffDays / 30)
    const days = diffDays % 30
    return `${months} months, ${days} days`
  }

  const visitDuration = firstVisitDate ? calculateDuration(firstVisitDate) : "-"

  // 3️⃣ Count total visits from timeline
  const totalVisits = safeApiData?.timeline ? Object.keys(safeApiData.timeline).length : 0

  // 4️⃣ Count total medicines (Prescriptions + Medical section)
  let totalMedicines = 0
  // From medical section
  if (safeApiData?.timeline) {
    Object.values(safeApiData.timeline).forEach((entry) => {
      if (entry.medical) {
        entry.medical.forEach((m) => {
          if (Array.isArray(m.Medicines)) {
            totalMedicines += m.Medicines.length
          }
        })
      }
    })
  }

  // Debug logging for data structure
  useEffect(() => {
    console.log("=== DATA STRUCTURE DEBUG ===")
    console.log("casePaperData:", casePaperData)
    console.log("apiData:", apiData)
    console.log("safeApiData:", safeApiData)
    console.log("safeApiData keys:", safeApiData ? Object.keys(safeApiData) : "No data")
    console.log("safeApiData?.latestNotes:", safeApiData?.latestNotes)
    console.log("safeApiData?.upcomingFollowups:", safeApiData?.upcomingFollowups)
    console.log("safeApiData?.upcomingVisits:", safeApiData?.upcomingVisits)
    console.log("safeApiData?.lastVisits:", safeApiData?.lastVisits)
    // Check if data might be nested differently
    if (safeApiData) {
      console.log("All available keys in safeApiData:", Object.keys(safeApiData))
      console.log("Checking for nested data...")
      Object.keys(safeApiData).forEach((key) => {
        if (typeof safeApiData[key] === "object" && safeApiData[key] !== null) {
          console.log(`${key} keys:`, Object.keys(safeApiData[key]))
        }
      })
    }
    console.log("=== END DEBUG ===")
  }, [casePaperData, apiData, safeApiData])

  const patientDetails = useSelector((state) => state.patients?.patientDetails)
  const notesFromStore = useSelector((state) => {
    return state.notes?.notes || []
  })

  const notes = safeApiData?.Notes || notesFromStore

  useEffect(() => {
    if (safeApiData) {
      console.log("Case paper API data:", safeApiData)
      console.log("Latest notes from API:", safeApiData.latestNotes)
      console.log("Notes from API:", safeApiData.Notes)
      console.log("Important questions from API:", safeApiData.ImportantQuestions)
    }
    console.log("Final notes being used:", notes)
  }, [safeApiData, notes])

  const [formData, setFormData] = useState()
  const [accordionOpen, setAccordionOpen] = useState({})
  const [activeFilters, setActiveFilters] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isCasePaperLoading, setIsCasePaperLoading] = useState(false)
  
  // Report image preview state
  const [imagePreview, setImagePreview] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0,
    reportId: null,
  })

  // Report carousel slides state - tracks current slide for each report by index
  const [reportSlides, setReportSlides] = useState({})

  // Notes filter state
  const [showDoctorNotesOnly, setShowDoctorNotesOnly] = useState(false)

  useEffect(() => {
    if (currentPatientId) {
      console.log("Loading patient details for ID:", currentPatientId)
      setIsLoading(true)
      dispatch(getPatientDetailsAction(currentPatientId))
        .then(() => {
          console.log("Patient details loaded successfully")
        })
        .catch((error) => {
          console.error("Failed to load patient details:", error)
          console.log("Continuing with stub data due to patient details API failure")
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      console.warn("No valid patient ID found in URL or params.")
      setIsLoading(false)
    }
  }, [dispatch, currentPatientId])

  useEffect(() => {
    // Use patient data from case paper API if available, otherwise use patient details API
    const patientInfo = safeApiData?.patient || patientDetails || patientData
    if (patientInfo) {
      const {
        first_name,
        middile_name,
        last_name,
        whatsapp_contact_no,
        patient_email,
        patient_photo,
        branch,
        additional_contact_no,
        date_of_birth,
        height,
        weight,
        occupation,
        id_proof,
        id_number,
        gender,
        husband_or_spouse_name,
        age,
        marriage_history,
        marriage_date,
        diseases,
        disease_status,
        spouse_option,
        marriageInfo,
        TemporaryAddress,
        PermanentAddress,
        OtherInfo,
      } = patientInfo
      console.log("patientInfo:", patientInfo)
      const diseaseNames =
        diseases && diseases.length > 1
          ? diseases.map((d) => d.disease_name).join(" | ")
          : diseases && diseases.length === 1
            ? diseases[0].disease_name
            : ""
      const diseaseIds = diseases?.map((d) => d.disease_id?.toString()) || []
      const latestMarriage =
        Array.isArray(marriageInfo) && marriageInfo.length > 0
          ? [...marriageInfo]
            .filter((m) => m.date_of_marriage)
            .sort((a, b) => new Date(b.date_of_marriage) - new Date(a.date_of_marriage))[0]
          : null
      setFormData({
        patient_id: patientInfo.patient_id || patientData?.patient_id || "",
        name: patientInfo.name || `${first_name || ""} ${middile_name || ""} ${last_name || ""}`,
        name1: patientInfo.name || `${first_name || ""} ${middile_name || ""} ${last_name || ""}`,
        Contact: patientInfo.contact || whatsapp_contact_no,
        email: patientInfo.email || patient_email,
        profileImage1: patientInfo.profile_image || patient_photo || defaultAvatar,
        branch: patientInfo.branch || branch,
        // additionalContact: patientInfo.additional_contact || additional_contact_no,
        additionalContact: patientInfo.whatsapp_contact_no?.trim()
          ? patientInfo.whatsapp_contact_no
          : patientInfo.additional_contact_no?.trim()
            ? patientInfo.additional_contact_no
            : "-",
        dob: patientInfo.dob || date_of_birth,
        height: patientInfo.height || height,
        weight: patientInfo.weight || weight,
        occupation: patientInfo.occupation || occupation,
        idProof: patientInfo.id_proof || id_proof,
        idNumber: patientInfo.id_number || id_number,
        gender: patientInfo.gender || gender,
        spouseName1: patientInfo.spouse_name || latestMarriage?.husband_or_spouse_name || husband_or_spouse_name || "",
        spouseName: patientInfo.spouse_name || latestMarriage?.husband_or_spouse_name || husband_or_spouse_name || "",
        age: patientInfo.age || age,
        marriageHistory: patientInfo.marriage_history || latestMarriage?.marriage_history || marriage_history || "",
        marriageDate: patientInfo.marriage_date || latestMarriage?.date_of_marriage || marriage_date || "",
        currentDisease: patientInfo.current_disease || diseaseNames,
        diseases: patientInfo.diseases || diseases,
        disease_names: patientInfo.diseases?.map((d) => d.disease_id?.toString()) || diseaseIds,
        diseaseStatus: patientInfo.disease_status || disease_status,
        spouseOption: patientInfo.spouse_option || latestMarriage?.spouse_option || spouse_option || "",
        center: patientInfo.center || branch,
        marriageInfo: marriageInfo || [],
        tempAddressLine1: patientInfo.temporary_address?.address_line1 || TemporaryAddress?.address_line1 || "",
        tempAddressLine2: patientInfo.temporary_address?.address_line2 || TemporaryAddress?.address_line2 || "",
        tempArea: patientInfo.temporary_address?.area || TemporaryAddress?.area || "",
        tempCity: patientInfo.temporary_address?.city || TemporaryAddress?.city || "",
        tempPincode: patientInfo.temporary_address?.pincode || TemporaryAddress?.pincode || "",
        tempState: patientInfo.temporary_address?.state || TemporaryAddress?.state || "",
        tempCountry: patientInfo.temporary_address?.country || TemporaryAddress?.country || "",
        permAddressLine1: patientInfo.permanent_address?.address_line1 || PermanentAddress?.address_line1 || "",
        permAddressLine2: patientInfo.permanent_address?.address_line2 || PermanentAddress?.address_line2 || "",
        permArea: patientInfo.permanent_address?.area || PermanentAddress?.area || "",
        permCity: patientInfo.permanent_address?.city || PermanentAddress?.city || "",
        permPincode: patientInfo.permanent_address?.pincode || PermanentAddress?.pincode || "",
        permState: patientInfo.permanent_address?.state || PermanentAddress?.state || "",
        permCountry: patientInfo.permanent_address?.country || PermanentAddress?.country || "",
        adName: patientInfo.other_info?.ad_name || OtherInfo?.ad_name || "",
        adSet: patientInfo.other_info?.ad_set || OtherInfo?.ad_set || "",
        campaignName: patientInfo.other_info?.campaign_name || OtherInfo?.campaign_name || "",
        leadSource: patientInfo.other_info?.lead_source || OtherInfo?.lead_source || "",
        status: patientInfo.other_info?.status || OtherInfo?.status || "",
        priority: patientInfo.other_info?.priority || OtherInfo?.priority || "",
        modifiedBy: patientInfo.other_info?.modified_by || OtherInfo?.modified_by || "",
        userType: patientInfo.other_info?.user_type || OtherInfo?.user_type || "",
        treatmentHistory: patientInfo.other_info?.treatment_history || OtherInfo?.treatment_history || "",
      })
    }
  }, [patientDetails, patientData, safeApiData])

  useEffect(() => {
    if (currentPatientId) {
      console.log("Loading notes for patient ID:", currentPatientId)
      dispatch(getNotesAction(currentPatientId))
        .then(() => {
          console.log("Notes loaded successfully")
        })
        .catch((error) => {
          console.error("Failed to load notes:", error)
          console.log("Continuing without notes due to API failure")
        })
    }
  }, [dispatch, currentPatientId])

  useEffect(() => {
    if (currentPatientId) {
      console.log("Loading case paper data for patient ID:", currentPatientId)
      setIsCasePaperLoading(true)
      dispatch(getCasePaperDataAction(currentPatientId))
        .then(() => {
          console.log("Case paper data loaded successfully")
        })
        .catch((error) => {
          console.error("Failed to load case paper data:", error)
          console.log("Continuing with stub data due to API failure")
        })
        .finally(() => {
          setIsCasePaperLoading(false)
        })
    }
  }, [dispatch, currentPatientId])

  // ✅ Helper to clean malformed patient photo URLs
  const getValidPhotoUrl = (photoUrl) => {
    if (!photoUrl) return defaultAvatar
    // If multiple "https://" exist, keep only the last valid part
    if ((photoUrl.match(/https:\/\//g) || []).length > 1) {
      const parts = photoUrl.split("https://")
      return "https://" + parts[parts.length - 1]
    }
    return photoUrl
  }

  const diseaseOptions = useMemo(() => {
    const diseases = safeApiData?.patient?.diseases || patientDetails?.diseases
    if (diseases && diseases.length > 0) {
      return diseases.map((disease) => ({
        value: disease.disease_id?.toString() || disease.disease_name,
        label: disease.disease_name || "Unknown Disease",
      }))
    }
    return []
  }, [safeApiData?.patient?.diseases, patientDetails?.diseases])

  const apiStatistics = safeApiData?.statistics || {}
  const [filteredAppointments, setFilteredAppointments] = useState([])
  const [sortDirection, setSortDirection] = useState("desc")

  const formatApiData = (rawData) => {
    if (!rawData) return []
    const formattedAppointments = []
    if (rawData.timeline) {
      Object.keys(rawData.timeline).forEach((date) => {
        const dayData = rawData.timeline[date]
        console.log(`[v0] Processing date ${date}:`, dayData)
        console.log(`[v0] GAPLD data for ${date}:`, dayData.gapld || dayData.GAPLD)
        console.log(`[v0] Menopause data for ${date}:`, dayData.menopause)
        console.log(`[v0] Male chart data for ${date}:`, dayData.malechart || dayData.male_chart)
            // Process followups to ensure followup_no and service_call_no are preserved
      const processedFollowups = (dayData.followups || []).map(followup => {
        // Handle both array and object structures
        if (Array.isArray(followup)) {
          return followup.map(f => ({
            ...f,
            followup_no: f.followup_no || '',
            service_call_no: f.service_call_no || ''
          }))
        } else {
          return {
            ...followup,
            followup_no: followup.followup_no || '',
            service_call_no: followup.service_call_no || ''
          }
        }
      }).flat()
        const appointment = {
          id: `appointment_${date}`,
          date: date,
          time: "",
          doctor: "",
          status: "Completed",
          notes: dayData.notes || [],
          prescriptions: dayData.prescription || [],
          reports: dayData.reports?.map((report) => ({
            ...report,
            reportFiles: (report.report_files || [])
              .filter((file) => file?.report_file_url)
              .map((file) => ({
                id: file.report_file_id,
                fileUrl: file.report_file_url,
                fileName: file.report_file,
              })),
          })) || [],
          panchakarma: dayData.panchakarma || [],
          // counsellor: dayData.counsellor || {},
          counsellor: Array.isArray(dayData.counsellor) ? dayData.counsellor[0] || {} : dayData.counsellor || {},
          medical: dayData.medical || [],
          followup_calls: processedFollowups,
          GAPLD: dayData.gapld || dayData.GAPLD || [],
          menopause: dayData.menopause || [],
          male_chart: dayData.malechart || dayData.male_chart || [],
          questionnaires: dayData.questionnaire || [],
        }
        formattedAppointments.push(appointment)
      })
    }
    const appointmentsWithGAPLD = formattedAppointments.filter((apt) => apt.GAPLD && apt.GAPLD.length > 0)
    return formattedAppointments
  }

  const displayAppointmentsFromApi = useMemo(() => {
    if (casePaperData) console.log("Processing data for appointments:", casePaperData)
    const result = formatApiData(casePaperData)
    console.log("Formatted appointments result:", result)
    return result
  }, [casePaperData])

  const followupCallCount =
    apiStatistics.followupCallCount ||
    displayAppointmentsFromApi.filter((app) => app.followup_calls && app.followup_calls.length > 0).length

  const calculateMarriageAge = (marriageDate) => {
    if (!marriageDate) return ""
    const today = new Date()
    const marriage = new Date(marriageDate)
    if (isNaN(marriage.getTime())) return ""
    let years = today.getFullYear() - marriage.getFullYear()
    let months = today.getMonth() - marriage.getMonth()
    let days = today.getDate() - marriage.getDate()
    // Adjust months and years if current day is before marriage day
    if (days < 0) {
      months--
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0)
      days += prevMonth.getDate()
    }
    if (months < 0) {
      years--
      months += 12
    }
    const weeks = Math.floor(days / 7)
    const parts = []
    if (years > 0) parts.push(`${years}y`)
    if (months > 0) parts.push(`${months}m`)
    if (weeks > 0) parts.push(`${weeks}w`)
    // ✅ if no parts, return empty (hides completely)
    return parts.length > 0 ? parts.join(", ") : ""
  }

  const [showMoreCasePaper, setShowMoreCasePaper] = useState(true)
  const [showDetails, setShowDetails] = useState(false)

  const summaryItems = [
    { 
      label: "Online Visits", 
      value: safeApiData?.patient?.OnlineVisitCount || 0 
    },
    { 
      label: "Offline Visits", 
      value: safeApiData?.patient?.OfflineVisitCount || 0 
    },
  {
    label: "Latest Follow-up Date",
    value: safeApiData?.patient?.latest_followup_date
      ? formatDate(safeApiData.patient.latest_followup_date)
      : "-",
  },
  {
    label: "Next Appointment",
    value: safeApiData?.patient?.next_appointment_date
      ? formatDate(safeApiData.patient.next_appointment_date)
      : "-",
  },

  ]

  const detailItems = [


  ]

  useEffect(() => {
    console.log("Filter effect triggered - activeFilters:", activeFilters, "appointments:", displayAppointmentsFromApi)
    let filtered = [...displayAppointmentsFromApi]

    // If no filters selected, show all appointments
    if (activeFilters.length === 0) {
      filtered = displayAppointmentsFromApi
    } else {
      // Filter appointments based on active filters (show if matches ANY filter)
      filtered = displayAppointmentsFromApi.filter((appointment) => {
        return activeFilters.some((filter) => {
          const filterLower = filter.toLowerCase()
          switch (filterLower) {
            case "prescription": {
              return appointment.prescriptions && appointment.prescriptions.length > 0
            }
            case "report": {
              return appointment.reports && appointment.reports.length > 0
            }
            case "notes": {
              return appointment.notes && appointment.notes.length > 0
            }
            case "counsellor": {
              return appointment.counsellor && Object.keys(appointment.counsellor).length > 0
            }
            case "medical": {
              return appointment.medical && Object.keys(appointment.medical).length > 0
            }
            case "followup": {
              return appointment.followup_calls && appointment.followup_calls.length > 0
            }
            case "questionnaire": {
              return appointment.questionnaires && appointment.questionnaires.length > 0
            }
            case "gapld": {
              return (
                (appointment.GAPLD && appointment.GAPLD.length > 0) ||
                (appointment.gapld && appointment.gapld.length > 0)
              )
            }
            case "menopause": {
              return appointment.menopause && appointment.menopause.length > 0
            }
            case "male_chart": {
              return appointment.male_chart && appointment.male_chart.length > 0
            }
            case "doctornotes": {
              return (
                appointment.notes &&
                appointment.notes.some((note) => note.note_type === "Doctor" || note.created_by === "Doctor")
              )
            }
            default:
              return false
          }
        })
      })
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.date?.split("/").reverse().join("-"))
      const dateB = new Date(b.date?.split("/").reverse().join("-"))
      return sortDirection === "asc" ? dateA - dateB : dateB - dateA
    })

    setFilteredAppointments(filtered)

  }, [activeFilters, sortDirection, displayAppointmentsFromApi])

  const handleFilterChange = (filter) => {
    setActiveFilters((prev) => {
      // Check if filter already exists
      const filterExists = prev.includes(filter)

      if (filterExists) {
        // Remove the filter (toggle off)
        return prev.filter((f) => f !== filter)
      } else {
        // Add the filter (toggle on)
        return [...prev, filter]
      }
    })
  }
  const openAllAccordions = () => {
    const allAccordionsOpen = {}
    filteredAppointments.forEach((_, index) => {
      allAccordionsOpen[index] = true
    })
    setAccordionOpen(allAccordionsOpen)
  }
  
  const closeAllAccordions = () => {
    setAccordionOpen({})
  }
  const toggleAccordion = (key) => {
    setAccordionOpen((prev) => {
      const updated = { ...prev }
  
      if (updated[key]) {
        delete updated[key]   // ✅ REMOVE = CLOSE
      } else {
        updated[key] = true   // ✅ ADD = OPEN
      }
  
      return updated
    })
  }

  // Report image preview functions
  const openImagePreview = (images, initialIndex = 0) => {
    setImagePreview({
      isOpen: true,
      images: images,
      currentIndex: initialIndex,
      reportId: null,
    })
  }

  const closeImagePreview = () => {
    setImagePreview({
      isOpen: false,
      images: [],
      currentIndex: 0,
      reportId: null,
    })
  }

  const goToNextImage = () => {
    if (imagePreview.currentIndex < imagePreview.images.length - 1) {
      setImagePreview((prev) => ({
        ...prev,
        currentIndex: prev.currentIndex + 1,
      }))
    }
  }

  const goToPreviousImage = () => {
    if (imagePreview.currentIndex > 0) {
      setImagePreview((prev) => ({
        ...prev,
        currentIndex: prev.currentIndex - 1,
      }))
    }
  }
  const formatDuration = (value) => {
    if (!value || value <= 0) return "-";
  
    const duration = parseFloat(value);
  
    if (duration < 1) {
      const days = Math.round(duration * 30);
      return `${days} day${days > 1 ? "s" : ""}`;
    }
  
    return `${duration} month${duration > 1 ? "s" : ""}`;
  };
  const renderGAPLDContent = (gapldData) => {
    if (!gapldData || gapldData.length === 0) {
      return <p>No GAPLD records available</p>
    }
    // Simple test render first
    const getActiveEntriesWithSequentialIds = () => {
      const activeEntries = gapldData
        .filter((entry) => entry.gapld_status !== "Delete")
        .sort((a, b) => Number.parseInt(a.gapld_id) - Number.parseInt(b.gapld_id))
      return activeEntries.map((entry, index) => ({
        ...entry,
        sequentialId: index + 1,
        displayId: `g${index + 1}`,
      }))
    }
    const activeEntries = getActiveEntriesWithSequentialIds()
    const calculateAge = (dateString) => {
      if (!dateString || dateString === "0000-00-00") return 0
      const birthDate = new Date(dateString)
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--
      }
      return age
    }
    const attributes = [
      "Abortion Month, Year, Date of Birth - Age",
      "Which RX helped to maintain pregnancy?",
      "How was the delivery?",
      "Male/Female",
      "How did the miscarriage happen?",
      "Miscarriage Reason",
    ]
    return (
      <div>
        <div
          style={{
            border: "2px solid #ddd",
            borderRadius: "5px",
            marginBottom: "24px",
            overflowX: "auto",
            backgroundColor: "white",
            display: "block", // Force display
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              display: "table", // Force table display
            }}
          >
            <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 10 }}>
              <tr style={{ backgroundColor: "#d1ecf1" }}>
                <th
                  style={{
                    padding: "10px",
                    color: "black",
                    fontWeight: "bold",
                    textAlign: "center",
                    border: "1px solid #ccc",
                  }}
                ></th>
                {activeEntries.map((entry) => (
                  <th
                    key={entry.displayId}
                    style={{
                      color: "black",
                      fontWeight: "bold",
                      padding: "10px",
                      backgroundColor: entry.sequentialId <= 2 ? "yellow" : "lightgreen",
                      textAlign: "center",
                      border: "1px solid #ccc",
                    }}
                  >
                    {entry.displayId}
                  </th>
                ))}
              </tr>
              <tr style={{ backgroundColor: "#fff3cd" }}>
                <th
                  style={{
                    color: "black",
                    fontWeight: "bold",
                    padding: "10px",
                    textAlign: "center",
                    border: "1px solid #ccc",
                  }}
                >
                  G= A+P
                </th>
                {activeEntries.map((entry) => (
                  <th
                    key={entry.displayId}
                    style={{
                      color: "black",
                      fontWeight: "bold",
                      padding: "10px",
                      textAlign: "center",
                      border: "1px solid #ccc",
                    }}
                  >
                    {entry.sequentialId <= 2 ? "A" + entry.sequentialId : "M" + (entry.sequentialId - 2)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody style={{ backgroundColor: "white" }}>
              {attributes.map((attribute, index) => (
                <tr key={index}>
                  <th
                    style={{
                      color: "black",
                      fontWeight: "normal",
                      padding: "10px",
                      textAlign: "left",
                      border: "1px solid #ccc",
                    }}
                  >
                    {attribute}
                  </th>
                  {activeEntries.map((entry) => {
                    const deliveryDate =
                      entry.delivery_date && entry.delivery_date !== "0000-00-00"
                        ? `${new Date(entry.delivery_date).toLocaleDateString("en-US")} - ${calculateAge(entry.delivery_date)} years`
                        : ""
                    const abortionDate =
                      entry.abortion_date && entry.abortion_date !== "0000-00-00"
                        ? new Date(entry.abortion_date).toLocaleDateString("en-US", { month: "short", year: "numeric" })
                        : ""
                    let value = ""
                    switch (index) {
                      case 0:
                        value =
                          entry.pregnancy_status === "Abortion /Miscarriage"
                            ? abortionDate || "Not specified"
                            : deliveryDate || "Not specified"
                        break
                      case 1:
                        value = entry.which_rx_helped || "Not specified"
                        break
                      case 2:
                        value =
                          entry.How_was_the_delivery ||
                          (entry.pregnancy_status === "Abortion /Miscarriage" ? "N/A" : "Not specified")
                        break
                      case 3:
                        value =
                          entry.gender || (entry.pregnancy_status === "Abortion /Miscarriage" ? "N/A" : "Not specified")
                        break
                      case 4:
                        value =
                          entry.pregnancy_status === "Abortion /Miscarriage"
                            ? entry.how_did_miscarriage_happen || "Not specified"
                            : "N/A"
                        break
                      case 5:
                        value =
                          entry.pregnancy_status === "Abortion /Miscarriage"
                            ? entry.miscarriage_reason || "Not specified"
                            : "N/A"
                        break
                    }
                    return (
                      <td
                        key={entry.displayId}
                        style={{ padding: "10px", textAlign: "center", border: "1px solid #ccc" }}
                      >
                        {value}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  const renderMenopauseContent = (menopauseData) => {
    if (!menopauseData || menopauseData.length === 0) {
      return <p>No menopause chart data available</p>
    }
    // const formatDate = (dateString) => {
    //   const date = new Date(dateString)
    //   const day = date.getDate()
    //   const month = date.toLocaleString("default", { month: "short" })
    //   const year = date.getFullYear()
    //   return { label: `${day} ${month}`, year: year.toString() }
    // }
    const attributes = [
      "Age of first menstruation",
      "Menstruation",
      "How long has menstruation stopped?",
      "What causes menstruation?",
      "How many days does menstruation occur?",
    ]
    return (
      <div style={{ border: "2px solid #ddd", borderRadius: "5px", marginBottom: "24px", overflowX: "auto" }}>
        <table className="table header-border mb-0" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 10 }}>
            <tr className="table-info">
              <th style={{ padding: "10px", color: "black", fontWeight: "bold", textAlign: "center" }}></th>
              {menopauseData.map((item) => {
                const formattedDate = formatDate(item.menopause_date)
                return (
                  <th
                    key={item.menopause_id}
                    style={{ color: "black", fontWeight: "bold", textAlign: "center", padding: "10px" }}
                  >
                    <div>{formattedDate.label}</div>
                    <div style={{ fontSize: "0.8em", color: "#6c757d" }}>{formattedDate.year}</div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody style={{ backgroundColor: "white", textAlign: "center" }}>
            {attributes.map((attr, index) => (
              <tr key={index}>
                <th style={{ textAlign: "left", fontWeight: "normal", padding: "10px" }}>{attr}</th>
                {menopauseData.map((item) => {
                  let value = ""
                  switch (index) {
                    case 0:
                      value = item.age_first_m
                      break
                    case 1:
                      value = item.menstruation
                      break
                    case 2:
                      value = item.how_long_m_stopped
                      break
                    case 3:
                      value = item.causes_m
                      break
                    case 4:
                      value = item.how_many_days_occur
                      break
                  }
                  return (
                    <td key={item.menopause_id} style={{ padding: "10px" }}>
                      {value}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // Helper function to render question answers including file inputs
  const renderQuestionAnswer = (q) => {
    // Check if it's a file input type
    if (q.question_type === "File Input" && q.answer) {
      return (
        <div className="mb-1">
          <a 
            href={q.answer} 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-primary"
            style={{ textDecoration: 'none' }}
          >
            📎 View {q.short_form || 'File'}
          </a>
        </div>
      )
    }
    // Regular text answer
    return <p className="mb-1">Answer: {q.answer}</p>
  }

  const renderMaleChartContent = (maleChartData) => {
    if (!maleChartData || maleChartData.length === 0) {
      return <p>No male chart data available</p>
    }
    const formatDateLocal = (dateString) => {
      if (!dateString) return { day: "", year: "" }
      const date = new Date(dateString)
      const day = date.getDate()
      const month = date.toLocaleString("default", { month: "short" })
      const year = date.getFullYear()
      return { day: `${day} ${month}`, year: year.toString() }
    }
    const tableDates = maleChartData.map((chart) => formatDateLocal(chart.malechart_date || chart.date))
    const tableRows = [
      {
        label: "Symptoms during/after intercourse?",
        values: maleChartData.map((chart) => chart.symptoms_intercourse || chart.symptoms || ""),
      },
      {
        label: "Metallic color",
        values: maleChartData.map((chart) => chart.metalic_color || chart.metallicColor || ""),
      },
    ]
    return (
      <div style={{ border: "2px solid #ddd", borderRadius: "5px", marginBottom: "24px", overflowX: "auto" }}>
        <table className="table header-border mb-0" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 10 }}>
            <tr className="table-info">
              <th style={{ padding: "10px", textAlign: "center" }}></th>
              {tableDates.map((date, idx) => (
                <th
                  key={idx}
                  style={{
                    color: "black",
                    fontWeight: "bold",
                    padding: "10px",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  <div>{date.day}</div>
                  <div style={{ fontSize: "0.8em", color: "#6c757d" }}>{date.year}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                <th
                  style={{
                    color: "black",
                    fontWeight: "normal",
                    padding: "10px",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  {row.label}
                </th>
                {row.values.map((value, colIndex) => (
                  <td key={colIndex} style={{ padding: "10px", textAlign: "center", verticalAlign: "middle" }}>
                    {value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const renderPrescriptionContent = (prescriptions) => {
    if (!prescriptions || prescriptions.length === 0) {
      return <p>No prescriptions available</p>
    }
    // Separate medicines into custom and non-custom
    const allMedicines = prescriptions.flatMap(
      (prescription) =>
        prescription.Medicines?.map((medicine) => ({
          ...medicine,
          prescription_index: prescriptions.indexOf(prescription),
        })) || [],
    )
    const regularMedicines = allMedicines.filter((medicine) => medicine.is_custom === "No")
    const customMedicines = allMedicines.filter((medicine) => medicine.is_custom === "Yes")
    return (
      <div>
        {/* Regular Medicines Table */}
        {/* Regular Medicines Table (updated headings to match the second table) */}
        {regularMedicines.length > 0 && (
          <div
            style={{
              border: "2px solid #ddd",
              borderRadius: "5px",
              marginBottom: "24px",
              overflowX: "auto",
            }}
          >
            <table className="table header-border mb-0">
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#f8f9fa",
                  zIndex: 10,
                  borderBottom: "2px solid #ddd",
                }}
              >
                <tr className="table-info">
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    Medicine Name
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    Dose
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    Frequency
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    Quantity
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    Delivered
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    Duration
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    How to Take
                  </th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: "white", textAlign: "center" }}>
                {regularMedicines.map((medicine, index) => (
                  <tr key={`regular-${medicine.prescription_index}-${index}`}>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                      className="text-break"
                    >
                      {medicine.medicine_name}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                      className="text-break"
                    >
                      {medicine.dose}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                      className="text-break"
                    >
                      {medicine.frequency}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      {medicine.qty}
                    </td>
                    {/* Delivered */}
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      {medicine.updated_qty}
                    </td>
                    {/* Status */}
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <span
                        className={`badge ${medicine.delivery_status === "DELIVERED" ? "bg-success" : "bg-warning"}`}
                      >
                        {medicine.delivery_status}
                      </span>
                    </td>
                    {/* Duration — try custom_duration_in_days first, fallback to duration */}
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      {medicine.custom_duration_in_days && medicine.custom_duration_in_days > 0
                        ? medicine.custom_duration_in_days
                        : medicine.duration && medicine.duration > 0
                        ? formatDuration(medicine.duration)
                        : "-"}``
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                      className="text-break"
                    >
                      {medicine.how_to_take}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Custom Medicines */}
        {customMedicines.length > 0 && (
          <div
            style={{
              border: "2px solid #ddd",
              borderRadius: "5px",
              marginBottom: "0",
              overflowX: "auto",
            }}
          >
            <h5 className="p-2 border-bottom mb-0">Custom Medicines</h5>
            <table className="table header-border mb-0">
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#f8f9fa",
                  zIndex: 10,
                  borderBottom: "2px solid #ddd",
                }}
              >
                <tr className="table-info">
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    Medicine Name
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    Dose
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      textAlign: "center",
                      verticalAlign: "middle",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Frequency
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    Quantity
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    Delivered
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    Duration
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      color: "black",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    How to Take?
                  </th>
                </tr>
              </thead>
              <tbody>
                {customMedicines.map((medicine, index) => (
                  <tr key={`custom-${medicine.prescription_index}-${index}`}>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                      className="text-break"
                    >
                      {medicine.medicine_name}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                      className="text-break"
                    >
                      {medicine.dose}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                      className="text-break"
                    >
                      {medicine.frequency}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      {medicine.qty}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      {medicine.updated_qty}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <span
                        className={`badge ${medicine.delivery_status === "DELIVERED" ? "bg-success" : "bg-warning"}`}
                      >
                        {medicine.delivery_status}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      {medicine.custom_duration_in_days || "N/A"}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                      className="text-break"
                    >
                      {medicine.how_to_take}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Show message if no medicines */}
        {regularMedicines.length === 0 && customMedicines.length === 0 && (
          <div className="text-center text-muted py-4">No medicines prescribed</div>
        )}
      </div>
    )
  }

  if (isLoading) {
    return (
      <>
        <Loader />
      </>
    )
  }

  return (
    <>
      <NavHader />
      <Header />
      {formData && (
        <div className="d-flex flex-column " style={{ marginLeft: "16px", marginRight: "16px", marginTop: "95px" }}>
          <div
            className="col-lg-12"
          // style={{
          //   position: "sticky",
          //   top: "75px",
          //   zIndex: 100,
          //   backgroundColor: "#f8f9fa",
          // }}
          >
            <div className="card shadow-sm rounded-3 p-3 p-md-4">
              <div className="row align-items-center">
                <div className="col-12 col-md-2 text-center mb-4 mb-md-0">
                  <img
                    alt="Profile"
                    className="rounded"
                    width={120}
                    height={120}
                    style={{ objectFit: "cover" }}
                    // src={formData?.profileImage1 || defaultAvatar}
                    src={getValidPhotoUrl(formData?.profileImage1) || "/placeholder.svg"}
                    onError={(e) => (e.currentTarget.src = defaultAvatar)}
                  />
                  <h5 className="fw-bold mt-3 mb-1 text-black">{formData?.name || "-"}</h5>
                  <div>
                    <span className="badge bg-light text-dark px-3 py-2 rounded-pill">
                      {formData.gender === "Male" ? (
                        <>
                          <svg className="me-1" width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M14 2H22V10H20V5.41L15.71 9.71C16.45 10.79 16.91 12.1 16.98 13.5C17.17 17.14 14.32 20.24 10.68 20.48C6.79 20.74 3.5 17.6 3.5 13.71C3.5 9.82 6.65 6.53 10.54 6.29C11.94 6.2 13.25 6.66 14.34 7.41L18.59 3H14V2Z"
                              fill="#2BC155"
                            />
                          </svg>
                          Male
                        </>
                      ) : formData.gender === "Female" ? (
                        <>
                          <svg className="me-1" width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M12 2C8.13 2 5 5.13 5 9C5 12.52 7.61 15.44 11 15.93V18H8V20H11V22H13V20H16V18H13V15.93C16.39 15.44 19 12.52 19 9C19 5.13 15.87 2 12 2ZM12 13C9.24 13 7 10.76 7 8C7 5.24 9.24 3 12 3C14.76 3 17 5.24 17 8C17 10.76 14.76 13 12 13Z"
                              fill="#C92A2A"
                            />
                          </svg>
                          Female
                        </>
                      ) : formData.gender === "Transgender" ? (
                        <>
                          <svg className="me-1" width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M19 3h-2.59l1.3 1.29-2.83 2.83c-.85-.54-1.84-.87-2.88-.98V4h1V2h-4v2h1v2.14c-2.79.47-5 2.93-5 5.86 0 3.31 2.69 6 6 6 .57 0 1.11-.08 1.63-.24l1.63 1.63V20h-2v2h4v-2h-1v-1.17l-1.41-1.41c1.48-.98 2.46-2.65 2.46-4.59 0-1.04-.29-2.02-.78-2.85l2.83-2.83L21 6.59V4h-2V3zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"
                              fill="#C92A2A"
                            />
                          </svg>
                          Transgender
                        </>
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>
                </div>
                <div className="col-12 col-md-10">
                  <div className="row g-3">
                    {[
                      { label: "Branch", value: formData.branch || formData.center || "-" },
                      { label: "Occupation", value: formData.occupation || "-" },
                      { label: "Contact Number", value: formData.additionalContact || "-" },
                      // { label: "Date of Birth", value: formData.dob || "-" },
                      {
                        label: "Date of Birth",
                        value: formData.dob && formData.dob !== "0000-00-00" ? formatDate(formData.dob) : "-",
                      },
                    ].map(({ label, value }) => (
                      <div key={label} className="col-12 col-sm-6 col-lg-3">
                        <h6 className="fw-semibold mb-1">{label}</h6>
                        <p className="mb-0">{value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="row g-3 mt-1 align-items-center">
                    {/* First three columns */}
                    {[
                      {
                        label: "Height (cm)",
                        value: formData.height && formData.height !== "0" ? formData.height : "-",
                      },
                      {
                        label: "Weight (kg)",
                        value: formData.weight && formData.weight !== "0" ? formData.weight : "-",
                      },
                      {
                        label: "Followup Call Count",
                        value: <span className="badge bg-primary rounded-pill fs-6">{followupCallCount}</span>,
                      },
                    ].map(({ label, value }) => (
                      <div key={label} className="col-6 col-lg-3">
                        <h6 className="fw-semibold mb-1">{label}</h6>
                        <p className="mb-0">{value}</p>
                      </div>
                    ))}
                    {/* Last column for button */}
                    <div className="col-6 col-lg-3 ">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setShowDetails(!showDetails)}
                        aria-expanded={showDetails}
                      >
                        {showDetails ? "Show Less Details" : "Show More Details"}
                      </button>
                    </div>
                  </div>

                  {/* -------------------- SUMMARY SECTION -------------------- */}
                  {/* -------------------- END SUMMARY SECTION -------------------- */}
                  {showMoreCasePaper && (
                    <>
                      {/* --- Summary + Expand Section --- */}
                      {showDetails && (
                        <>
                          <div className="row g-3 mt-1">
                            {[
                              // {
                              //   label: "With Spouse",
                              //   value: formData.spouseOption === "withSpouse" ? "Yes" : "No",
                              // },
                              // {
                              //   label: "Husband/Spouse Name",
                              //   value: formData.spouseName1 || formData.spouseName || "-",
                              // },
                              {
                                label: "With Spouse",
                                value: (() => {
                                  const option =
                                    formData.spouseOption ||
                                    formData.spouse_option ||
                                    formData.marriageInfo?.[0]?.spouse_option ||
                                    "-"
                                  if (!option || option === "-") return "-"
                                  const normalized = option.toLowerCase().replace(/\s+/g, "")
                                  return normalized === "withspouse" ? "Yes" : "No"
                                })(),
                              },
                              {
                                label: "Husband/Spouse Name",
                                value: (() => {
                                  // Prefer explicit name, else use latest marriage info's name fields
                                  const name =
                                    formData.spouseName1?.trim() ||
                                    formData.spouseName?.trim() ||
                                    (Array.isArray(formData.marriageInfo) &&
                                      formData.marriageInfo.length > 0 &&
                                      (() => {
                                        const latestMarriage = [...formData.marriageInfo]
                                          .filter((m) => m.date_of_marriage)
                                          .sort(
                                            (a, b) => new Date(b.date_of_marriage) - new Date(a.date_of_marriage),
                                          )[0]
                                        return (
                                          latestMarriage?.husband_or_spouse_name ||
                                          [
                                            latestMarriage?.first_name,
                                            latestMarriage?.middile_name,
                                            latestMarriage?.last_name,
                                          ]
                                            .filter(Boolean)
                                            .join(" ")
                                        )
                                      })()) ||
                                    "-"
                                  return name || "-"
                                })(),
                              },
                              {
                                label: "Marriage History",
                                value: formData.marriageHistory || "-",
                              },
                              // {
                              //   label: "Marriage Date",
                              //   value: `${formatDate(formData.marriageDate)} (${calculateMarriageAge(formData.marriageDate)})`,
                              // },
                              {
                                label: "Marriage Date",
                                value:
                                  formData.marriageDate && formData.marriageDate !== "0000-00-00"
                                    ? `${formatDate(formData.marriageDate)} (${calculateMarriageAge(formData.marriageDate)})`
                                    : "-",
                              },
                            ].map(({ label, value }) => (
                              <div key={label} className="col-12 col-sm-6 col-lg-3">
                                <h6 className="fw-semibold mb-1">{label}</h6>
                                <p className="mb-0">{value}</p>
                              </div>
                            ))}
                          </div>
                          <div className="row g-3 mt-1">
                          <div className="col-6 col-md-3">
  <h6 className="fw-semibold mb-1">First Visit</h6>
  <p className="mb-0">
  {safeApiData?.patient?.first_appointment
  ? formatDate(safeApiData.patient.first_appointment)
  : "-"}
  </p>
</div>

<div className="col-6 col-md-3">
  <h6 className="fw-semibold mb-1">Duration Since First Visit</h6>
  <p className="mb-0">
    {safeApiData?.patient?.duration_first_appointment || "-"}
  </p>
</div>

<div className="col-6 col-md-3">
  <h6 className="fw-semibold mb-1">Number of Visits</h6>
  <p className="mb-0">
    {safeApiData?.patient?.no_of_visit || "0"}
  </p>
</div>

<div className="col-6 col-md-3">
  <h6 className="fw-semibold mb-1">Medicines Taken</h6>
  <p className="mb-0">
    {safeApiData?.patient?.no_of_medicine_taken || "0"}
  </p>
</div>
                          </div>
                          {/* Summary Section */}
                          <div className="row g-3 mt-1">
                            {summaryItems.map((item, index) => (
                              <div className="col-6 col-md-3" key={index}>
                                <h6 className="fw-semibold mb-1">{item.label}</h6>
                                <p className="mb-0">{item.value}</p>
                              </div>
                            ))}
                          </div>
                          {/* Expanded Detail Section */}
                      
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex-grow-1 overflow-auto">
            <div className="container-fluid py-3">
              <div className="row g-3">
                <div className="col-lg-8">
                  <div className="card" style={{ width: "100%" }}>
                    <div className="card-header border-0 pb-0">
                      <h4 className="fs-18 text-black font-w600 mb-0">Notes</h4>
                    </div>
                    <div className="card-body pt-1 pb-1"> {/* reduce padding */}
  <div id="DZ_W_Todo2" className="widget-media dz-scroll height400">
      <NotesHistory casePaperData={safeApiData} />
  </div>
</div>

                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="card" style={{ width: "100%" }}>
                    <ImportantQuestion casePaperData={safeApiData}></ImportantQuestion>
                  </div>
                </div>
                <div className="col-xl-6">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="fs-22 text-black font-w600 mb-0">Platform</h4>
                      </div>
                      <div className="d-flex flex-row justify-content-start align-items-center mb-3">
                        <div className="media me-3">
                          <div className="media-body">
                            <h5 className="mt-0 vertical-text bg-primary rounded-pill px-3 py-2 text-white">
                              Instagram
                            </h5>
                          </div>
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-start gap-4">
                        {/* LEFT COLUMN — Upcoming Follow Ups + Latest Notes */}
                        <div className="flex-fill">
                          {/* Upcoming Follow Ups */}
                          <div className="mb-4">
                            <h5 className="fs-18 font-w600">Upcoming Follow Ups :</h5>
                            <div className="subheading">
                              {isCasePaperLoading ? (
                                <p className="text-muted">Loading...</p>
                              ) : safeApiData?.upcomingFollowups || casePaperData?.upcomingFollowups ? (
                                <>
                                  <p className="mb-1">
                                    <strong>Follow-up Count:</strong>{" "}
                                    {safeApiData?.upcomingFollowups?.followup_count ||
                                      casePaperData?.upcomingFollowups?.followup_count}
                                  </p>
                                  <p className="mb-1">
                                    <strong>Status:</strong>{" "}
                                    {safeApiData?.upcomingFollowups?.schedule_status ||
                                      casePaperData?.upcomingFollowups?.schedule_status}
                                  </p>
                                  <p className="mb-1">
                                    <strong>Next Follow-Up:</strong>{" "}
                                    {formatDate(
                                      safeApiData?.upcomingFollowups?.followup_date ||
                                      casePaperData?.upcomingFollowups?.followup_date,
                                    )}
                                  </p>
                                  {(safeApiData?.upcomingFollowups?.followup_frequency_days ||
                                    casePaperData?.upcomingFollowups?.followup_frequency_days) && (
                                      <p className="mb-1">
                                        <strong>Frequency:</strong> Every{" "}
                                        {safeApiData?.upcomingFollowups?.followup_frequency_days ||
                                          casePaperData?.upcomingFollowups?.followup_frequency_days}{" "}
                                        days
                                      </p>
                                    )}
                                  {(safeApiData?.upcomingFollowups?.delay_days ||
                                    casePaperData?.upcomingFollowups?.delay_days) &&
                                    Number.parseInt(
                                      safeApiData?.upcomingFollowups?.delay_days ||
                                      casePaperData?.upcomingFollowups?.delay_days,
                                    ) > 0 && (
                                      <p className="mb-1 text-warning">
                                        <strong>Delay:</strong>{" "}
                                        {safeApiData?.upcomingFollowups?.delay_days ||
                                          casePaperData?.upcomingFollowups?.delay_days}{" "}
                                        days overdue
                                      </p>
                                    )}
                                </>
                              ) : (
                                <p className="text-muted">No upcoming follow-ups scheduled</p>
                              )}
                            </div>
                          </div>
                          {/* Latest Notes */}
                          <div>
                            <h5 className="fs-18 font-w600">Latest Notes:</h5>
                            <div className="subheading">
                              {isCasePaperLoading ? (
                                <p className="text-muted">Loading...</p>
                              ) : safeApiData?.latestNotes?.length > 0 || casePaperData?.latestNotes?.length > 0 ? (
                                <div className="mb-2">
                                  <p className="mb-1 d-flex align-items-center">
                                    {safeApiData?.latestNotes?.[0]?.note_description ||
                                      casePaperData?.latestNotes?.[0]?.note_description ||
                                      safeApiData?.latestNotes?.[0]?.description ||
                                      casePaperData?.latestNotes?.[0]?.description ||
                                      "No description available"}
                                    <span className="badge bg-primary rounded-pill fs-6 ms-2">1</span>
                                  </p>
                                  <p className="fs-15 mb-0">
                                    Date:{" "}
                                    {formatDate(
                                      safeApiData?.latestNotes?.[0]?.created_at ||
                                      casePaperData?.latestNotes?.[0]?.created_at,
                                    )}
                                  </p>
                                </div>
                              ) : (
                                <p className="text-muted">No notes available</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-6">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="fs-22 text-black font-w600 mb-0">Diseases</h4>
                      </div>
                      <div className="d-flex flex-row justify-content-start align-items-center mb-4">
                        {diseaseOptions.length > 0 ? (
                          diseaseOptions.map((option) => (
                            <div className="media me-4" key={option.value}>
                              <div className="media-body">
                                <h5 className="mt-0 vertical-text bg-primary rounded-pill px-3 py-2 text-white">
                                  {option.label}
                                </h5>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-muted">No diseases recorded</div>
                        )}
                      </div>
                      <div className="mb-4">
                        <h5 className="fs-18 font-w600">Upcoming Visits:</h5>
                        <div className="subheading">
                          {isCasePaperLoading ? (
                            <p className="text-muted">Loading...</p>
                          ) : safeApiData?.upcomingVisits && safeApiData?.upcomingVisits.date ? (
                            <>
                              <p className="mb-1"><strong>Type:</strong> {safeApiData?.upcomingVisits.appointment_type}</p>
                              <p className="mb-1"><strong>Date:</strong> {formatDate(safeApiData?.upcomingVisits.date)}</p>
                              <p className="mb-0">
  <strong>Status:</strong>{' '}
  <span className="badge bg-info ">
    {safeApiData?.upcomingVisits.appointment_status || 
     safeApiData?.upcomingVisits.queue_appointment_status || 
     'N/A'}
  </span>
</p>
                            </>
                          ) : (
                            <p className="text-muted">No upcoming visits scheduled</p>
                          )}
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-start mb-4">
                        {/* LEFT SIDE — LAST VISITS */}
                        <div>
                          <h5 className="fs-18 font-w600">Last Visits:</h5>
                          <div className="subheading">
                            {isCasePaperLoading ? (
                              <p className="text-muted">Loading...</p>
                            ) : safeApiData?.lastVisits && safeApiData?.lastVisits.date ? (
                              <>
                                {/* <p className="mb-1"><strong>Type:</strong> {safeApiData?.lastVisits.appointment_type}</p> */}
                                <p className="mb-1">
                                  <strong>Date:</strong> {formatDate(safeApiData?.lastVisits.date)} {safeApiData?.lastVisits.time_slot ? `(${safeApiData?.lastVisits.time_slot})` : ''}
                                </p>
                                <p className="mb-0">
  <strong>Status:</strong>{' '}
  <span className="badge bg-info ">
    {safeApiData?.lastVisits.appointment_status || 
     safeApiData?.lastVisits.queue_appointment_status || 
     'N/A'}
  </span>
</p>
                              </>
                            ) : (
                              <p className="text-muted">No previous visits found</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Col xl="12">
                  <div
                    className="card shadow-none"

                  >
                    <div className="card-body">
                      {Array.isArray(displayAppointmentsFromApi) && displayAppointmentsFromApi.length > 0 && (
                        <div
                          className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-4 gap-3"
                          style={{
                            position: "sticky",
                            top: "95px",
                            zIndex: 100,
                            backgroundColor: "white",
                            padding: "15px 0",
                            margin: "-15px 0 15px 0",
                          }}
                        >
                          <div className="d-flex flex-wrap gap-2 w-100">
                            <button
                              className={`btn btn-sm ${activeFilters.length === 0 ? "btn-primary" : "btn-outline-primary"}`}
                              onClick={() => setActiveFilters([])}
                            >
                              All
                            </button>
                            <button
                              className={`btn btn-sm ${activeFilters.includes("Prescription") ? "btn-primary" : "btn-outline-primary"}`}
                              onClick={() => handleFilterChange("Prescription")}
                            >
                              Prescription
                            </button>
                            <button
                              className={`btn btn-sm ${activeFilters.includes("Report") ? "btn-primary" : "btn-outline-primary"}`}
                              onClick={() => handleFilterChange("Report")}
                            >
                              Reports
                            </button>
                            <button
                              className={`btn btn-sm ${activeFilters.includes("Notes") ? "btn-primary" : "btn-outline-primary"}`}
                              onClick={() => handleFilterChange("Notes")}
                            >
                              Notes
                            </button>
                            <button
                              className={`btn btn-sm ${activeFilters.includes("Counsellor") ? "btn-primary" : "btn-outline-primary"}`}
                              onClick={() => handleFilterChange("Counsellor")}
                            >
                              Counsellor
                            </button>
                            <button
                              className={`btn btn-sm ${activeFilters.includes("Medical") ? "btn-primary" : "btn-outline-primary"}`}
                              onClick={() => handleFilterChange("Medical")}
                            >
                              Medical
                            </button>
                            <button
                              className={`btn btn-sm ${activeFilters.includes("Followup") ? "btn-primary" : "btn-outline-primary"}`}
                              onClick={() => handleFilterChange("Followup")}
                            >
                              Followup
                            </button>
                            {casePaperData?.patient?.gender === "Male" && (
                              <button
                                className={`btn btn-sm ${activeFilters.includes("male_chart") ? "btn-primary" : "btn-outline-primary"}`}
                                onClick={() => handleFilterChange("male_chart")}
                              >
                                Male Chart
                              </button>
                            )}
                            {casePaperData?.patient?.gender === "Female" && (
                              <>
                                <button
                                  className={`btn btn-sm ${activeFilters.includes("GAPLD") ? "btn-primary" : "btn-outline-primary"}`}
                                  onClick={() => handleFilterChange("GAPLD")}
                                >
                                  GAPLD
                                </button>
                                <button
                                  className={`btn btn-sm ${activeFilters.includes("Menopause") ? "btn-primary" : "btn-outline-primary"}`}
                                  onClick={() => handleFilterChange("Menopause")}
                                >
                                  Menopause
                                </button>
                              </>
                            )}
                            <button
                              className={`btn btn-sm ${activeFilters.includes("DoctorNotes") ? "btn-primary" : "btn-outline-primary"}`}
                              onClick={() => handleFilterChange("DoctorNotes")}
                            >
                              Doctor Notes
                            </button>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={openAllAccordions}
                            >
                              All Open
                            </button>

                          </div>
                          <Dropdown className="w-auto">
                            <Dropdown.Toggle variant="outline-info" className="d-flex align-items-center gap-1 btn-sm">
                              Sort by Date {sortDirection === "asc" ? "↑" : "↓"}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item active={sortDirection === "asc"} onClick={() => setSortDirection("asc")}>
                                Ascending ↑
                              </Dropdown.Item>
                              <Dropdown.Item active={sortDirection === "desc"} onClick={() => setSortDirection("desc")}>
                                Descending ↓
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      )}
                      <div
                        style={{
                          maxHeight: "70vh",
                          overflowY: "auto",
                          paddingRight: "10px",
                        }}
                      >
                        <Accordion className="accordion accordion-primary-solid" alwaysOpen activeKey={Object.keys(accordionOpen)}>
                          {filteredAppointments.length > 0 ? (
                            filteredAppointments.map((appointment, index) => {
                              const { date } = appointment
                              return (
                                <Accordion.Item eventKey={index.toString()}>
                                  <Accordion.Header  onClick={() => toggleAccordion(index.toString())} className="accordion-header">
                                    {/* <div className="d-flex justify-content-between align-items-center w-100">
                                    <div className="text-break">
                                      <span className="d-none d-sm-inline">Date: </span>
                                      <strong>{formatDate(date)}</strong>
                                    </div>
                                  </div> */}
                                    <div className="d-flex justify-content-between align-items-center w-100">
                                      <div className="text-break">
                                        <span className="d-none d-sm-inline">Date: </span>
                                        <strong>{formatDate(date)}</strong>

                                            {/* DYNAMIC FOLLOW-UP AND SERVICE CALL NUMBERS */}
                                            {appointment.followup_calls &&
 appointment.followup_calls.length > 0 &&
 (appointment.followup_calls[0]?.followup_no ||
  appointment.followup_calls[0]?.service_call_no) && (
  <span className="ms-3">
    {appointment.followup_calls[0]?.followup_no && (
      <>
        Follow-up: <strong>{appointment.followup_calls[0].followup_no}</strong>
      </>
    )}

    {appointment.followup_calls[0]?.service_call_no && (
      <>
        <span style={{ marginLeft: "20px" }}></span>
        Service Call No: <strong>{appointment.followup_calls[0].service_call_no}</strong>
      </>
    )}
  </span>
)}
                                      </div>
                                    </div>

                                  </Accordion.Header>
                                  <Accordion.Collapse eventKey={`${index}`} className="accordion__body">
                                    <div className="accordion-body">
                                      {activeFilters.length === 0 && (
                                        <>
                                          {appointment.notes.map((note, noteIndex) => (
                                            <div key={noteIndex} className="mb-4">
                                              <h5 className="fw-bold d-flex align-items-center fs-6">
                                                {"Note"}
                                                <span className="badge bg-primary rounded-pill fs-6 ms-2">
                                                  {note.index}
                                                </span>
                                              </h5>
                                              <div className="p-2 p-md-3 rounded bg-light-primary border shadow-sm">
                                                <p className="mb-0 text-dark text-break">{note.note_description}</p>
                                                <div className="mt-2 d-flex flex-wrap gap-1">
                                                  {note.is_important === "Yes" && (
                                                    <span className="badge bg-warning">Important</span>
                                                  )}
                                                  {note.is_emergency === "Yes" && (
                                                    <span className="badge bg-danger">Emergency</span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                          {appointment.prescriptions && appointment.prescriptions.length > 0 && (
                                            <div className="mb-4">
                                              <h5 className="fw-bold fs-6">Prescriptions</h5>
                                              <div className="p-2 p-md-3 rounded bg-light-primary border shadow-sm">
                                                {renderPrescriptionContent(appointment.prescriptions)}
                                              </div>
                                            </div>
                                          )}
                                          {appointment.panchakarma?.length > 0 && (
                                            <div className="mb-4">
                                              <h5 className="fw-bold fs-6">Panchakarma</h5>
                                              {appointment.panchakarma?.map((pk, pkIndex) => (
                                                <div
                                                  key={pkIndex}
                                                  className="p-2 p-md-3 rounded bg-light-success border shadow-sm"
                                                >
                                                  <h6 className="fs-6">{pk.panchakarma_name}</h6>
                                                  <p className="mb-1">Duration: {pk.duration}</p>
                                                  <p className="mb-1">Status: {pk.status}</p>
                                                  <p className="mb-1">Doctor: {pk.doctor_name}</p>
                                                  <p className="mb-1 text-break">Notes: {pk.notes}</p>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          {appointment.reports.length > 0 && (
                                            <div className="mb-4">
                                              <h5 className="fw-bold fs-6">Reports</h5>
                                              {appointment.reports.map((report, reportIndex) => {
                                                // Determine which files to display (new format: reportFiles array, or old format: fileUrl)
                                                const filesToDisplay = (report.reportFiles && report.reportFiles.length > 0)
                                                  ? report.reportFiles.filter(f => f && (f.fileUrl || f.report_file_url) && (f.fileUrl || f.report_file_url).trim() !== "").map(f => ({
                                                      fileUrl: f.fileUrl || f.report_file_url,
                                                      fileName: f.fileName || f.report_file || "",
                                                      id: f.id || f.report_file_id || ""
                                                    }))
                                                  : (report.fileUrl && report.fileUrl.trim() !== "" ? [{
                                                      fileUrl: report.fileUrl,
                                                      fileName: "Report File",
                                                      id: report.id
                                                    }] : [])
                                                
                                                // Skip rendering if no valid files
                                                if (!filesToDisplay || filesToDisplay.length === 0) return null
                                                
                                                return (
                                                  <div
                                                    key={reportIndex}
                                                    className="p-2 p-md-3 rounded bg-light-info border shadow-sm mb-3"
                                                  >
                                                    <div className="mb-2">
                                                      <p className="mb-1"><strong>{report.title || report.description || "Report"}</strong></p>
                                                      {report.description && report.title && <p className="mb-1 text-muted">{report.description}</p>}
                                                     
                                                    </div>
                                                    
                                                    {filesToDisplay.length > 0 && (
                                                      <div className="mt-2">
                                                        <h6 className="fs-6">Attached Files:</h6>
                                                        <div className="row g-2">
                                                          {filesToDisplay.map((file, fileIndex) => {
                                                            const fileUrl = file.fileUrl || file;
                                                            // Check if URL is valid
                                                            if (!fileUrl || fileUrl.trim() === "") return null;
                                                            
                                                            return (
                                                              <div key={fileIndex} className="col-12 col-sm-6">
                                                              <div 
                                                                style={{ 
                                                                  cursor: 'pointer', 
                                                                  position: 'relative',
                                                                  overflow: 'hidden',
                                                                  borderRadius: '4px'
                                                                }}
                                                                onClick={() => openImagePreview(filesToDisplay.map(f => f.fileUrl || f), fileIndex)}
                                                              >
                                                                <img
                                                                  src={fileUrl}
                                                                  alt={`Report ${reportIndex + 1} - File ${fileIndex + 1}`}
                                                                  className="img-thumbnail w-100"
                                                                  style={{
                                                                    height: "200px",
                                                                    objectFit: "cover",
                                                                    transition: 'transform 0.2s',
                                                                  }}
                                                                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                                                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                                                  onError={(e) => {
                                                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="%23999"%3EImage Not Available%3C/text%3E%3C/svg%3E'
                                                                  }}
                                                                />
                                                                <div 
                                                                  style={{
                                                                    position: 'absolute',
                                                                    top: '50%',
                                                                    left: '50%',
                                                                    transform: 'translate(-50%, -50%)',
                                                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                                                    color: 'white',
                                                                    padding: '8px 12px',
                                                                    borderRadius: '4px',
                                                                    fontSize: '12px',
                                                                    opacity: 0,
                                                                    transition: 'opacity 0.2s',
                                                                    pointerEvents: 'none'
                                                                  }}
                                                                  className="preview-hint"
                                                                >
                                                                  Click to preview
                                                                </div>
                                                                <style>{`
                                                                  .preview-hint:hover {
                                                                    opacity: 1 !important;
                                                                  }
                                                                `}</style>
                                                              </div>
                                                              {filesToDisplay.length > 1 && (
                                                                <p className="text-center small text-muted mt-2">
                                                                  File {fileIndex + 1} of {filesToDisplay.length}
                                                                </p>
                                                              )}
                                                            </div>
                                                            )
                                                          })}
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                          {appointment.counsellor && Object.keys(appointment.counsellor).length > 0 && (
                                            <div className="mb-4">
                                              <h5 className="fw-bold fs-6">Counsellor Session</h5>
                                              <div className="p-2 p-md-3 rounded bg-light-warning border shadow-sm">
                                                <p className="mb-1">
                                                  <strong>Session Date:</strong>{" "}
                                                  {formatDate(appointment.counsellor.consulting_date) || "Not specified"}
                                                </p>
                                                <p className="mb-1">
                                                  <strong>Medicine Explained Date:</strong>{" "}
                                                  {formatDate(appointment.counsellor.medicine_explained_date) ||
                                                    "Not specified"}
                                                </p>
                                                <p className="mb-1">
                                                  <strong>Medicine Explained By:</strong>{" "}
                                                  {appointment.counsellor.medicine_explined_by || "Not specified"}
                                                </p>
                                                {appointment.counsellor.recommendations &&
                                                  appointment.counsellor.recommendations.length > 0 && (
                                                    <div>
                                                      <strong>Recommendations:</strong>
                                                      <ul className="mb-0">
                                                        {appointment.counsellor.recommendations.map((rec, recIndex) => (
                                                          <li key={recIndex} className="text-break">
                                                            {rec}
                                                          </li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}
                                              </div>
                                            </div>
                                          )}
                                          {appointment.medical &&
                                            appointment.medical.length > 0 &&
                                            appointment.medical.map((medical, index) => (
                                              <div key={index} className="mb-4">
                                                <h5 className="fw-bold fs-6">Medical Record</h5>
                                                <div className="p-2 p-md-3 rounded bg-light-info border shadow-sm">
                                                  <p className="mb-1">
                                                    <strong>Medicine Prepared By:</strong>{" "}
                                                    {formatDate(medical.medicine_prepared_by) || "Not specified"}
                                                  </p>
                                                  <p className="mb-1">
                                                    <strong>Medicine Send Date:</strong>{" "}
                                                    {formatDate(medical.medicne_send_date) || "Not specified"}
                                                  </p>
                                                  <p className="mb-1">
                                                    <strong>Delivery Date:</strong>{" "}
                                                    {formatDate(medical.delivery_date) || "Not specified"}
                                                  </p>
                                                  {medical.Medicines && medical.Medicines.length > 0 && (
                                                    <div>
                                                      <strong>Medicines:</strong>
                                                      <ul className="mb-0">
                                                        {medical.Medicines.map((medicine, medIndex) => (
                                                          <li key={medIndex} className="text-break">
                                                            {medicine.medicine_name} - {medicine.delivery_status}
                                                          </li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          {appointment.followup_calls && appointment.followup_calls.length > 0 && (
                                            <div className="mb-4">
                                              <h5 className="fw-bold fs-6">Follow-up Calls</h5>
                                              {appointment.followup_calls.map((followup, fIndex) => (
                                                <div
                                                  key={fIndex}
                                                  className="p-2 p-md-3 rounded bg-light-success border shadow-sm mb-3"
                                                >
                                                  <h6 className="fs-6">Follow-up ID: {followup.followup_id}</h6>
                                                  {followup.questionnaire && followup.questionnaire.length > 0 && (
                                                    <div>
                                                      <strong>Questionnaire:</strong>
                                                      {followup.questionnaire.map((q, qIdx) => (
                                                        <div key={qIdx} className="mt-3">
                                                          {q.isGroup === "Yes" &&
                                                            q.questions &&
                                                            q.questions.length > 0 ? (
                                                            <div className="card border-primary mb-3">
                                                              <div className="card-header bg-light-primary">
                                                                <h6 className="fw-bold text-primary mb-0">
                                                                  {q.group_name || `Group ${q.group_id}`}
                                                                </h6>
                                                              </div>
                                                              <div className="card-body">
                                                                {q.questions.map((groupQuestion, gqIdx) => (
                                                                  <div key={gqIdx} className="mb-3">
                                                                    <p className="mb-1">
                                                                      <strong>{groupQuestion.question}</strong>
                                                                    </p>
                                                                    {groupQuestion.question_type === "File Input" && groupQuestion.answer ? (
                                                                      <div className="mb-1">
                                                                        <a 
                                                                          href={groupQuestion.answer} 
                                                                          target="_blank" 
                                                                          rel="noopener noreferrer"
                                                                          className="btn btn-sm btn-outline-primary"
                                                                          style={{ textDecoration: 'none' }}
                                                                        >
                                                                          📎 View {groupQuestion.short_form || 'File'}
                                                                        </a>
                                                                      </div>
                                                                    ) : (
                                                                      <p className="mb-1">Answer: {groupQuestion.answer}</p>
                                                                    )}
                                                                    {groupQuestion.is_important === "Yes" && (
                                                                      <span className="badge bg-warning">Important</span>
                                                                    )}
                                                                    {groupQuestion.note_description && (
                                                                      <p className="text-muted mt-1">
                                                                        <small>
                                                                          Note: {groupQuestion.note_description}
                                                                        </small>
                                                                      </p>
                                                                    )}
                                                                  </div>
                                                                ))}
                                                              </div>
                                                            </div>
                                                          ) : (
                                                            <div>
                                                              <p className="mb-1">
                                                                <strong>{q.question}</strong>
                                                              </p>
                                                              {renderQuestionAnswer(q)}
                                                              {q.is_important === "Yes" && (
                                                                <span className="badge bg-warning">Important</span>
                                                              )}
                                                              {q.note_description && (
                                                                <p className="text-muted mt-1">
                                                                  <small>Note: {q.note_description}</small>
                                                                </p>
                                                              )}
                                                            </div>
                                                          )}
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                  {followup.notes && followup.notes.length > 0 && (
                                                    <div>
                                                      <strong>Notes:</strong>
                                                      {followup.notes.map((note, nIdx) => (
                                                        <div key={nIdx} className="mt-2">
                                                          <p className="text-break mb-1">{note.note_description}</p>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          {appointment.GAPLD && appointment.GAPLD.length > 0 && (
                                            <div className="mb-4">
                                              <h5 className="fw-bold fs-6">GAPLD</h5>
                                              <div
                                                style={{
                                                  padding: "12px 16px",
                                                  borderRadius: "8px",
                                                  backgroundColor: "#d4edda",
                                                  border: "1px solid #c3e6cb",
                                                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                  display: "block",
                                                  minHeight: "50px",
                                                }}
                                              >
                                                {renderGAPLDContent(appointment.GAPLD)}
                                              </div>
                                            </div>
                                          )}
                                          {appointment.menopause && appointment.menopause.length > 0 && (
                                            <div className="mb-4">
                                              <h5 className="fw-bold fs-6">Menopause</h5>
                                              <div className="p-2 p-md-3 rounded bg-light-info border shadow-sm">
                                                {renderMenopauseContent(appointment.menopause)}
                                              </div>
                                            </div>
                                          )}
                                          {appointment.male_chart && appointment.male_chart.length > 0 && (
                                            <div className="mb-4">
                                              <h5 className="fw-bold fs-6">Male Chart</h5>
                                              <div className="p-2 p-md-3 rounded bg-light-warning border shadow-sm">
                                                {renderMaleChartContent(appointment.male_chart)}
                                              </div>
                                            </div>
                                          )}
                                        </>
                                      )}
                                      {activeFilters.includes("Prescription") && (
                                        <>
                                          {appointment.prescriptions && appointment.prescriptions.length > 0 ? (
                                            <div className="mb-4">
                                              <h5 className="fw-bold">Prescriptions</h5>
                                              <div className="p-3 rounded bg-light-primary border shadow-sm">
                                                {renderPrescriptionContent(appointment.prescriptions)}
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="p-3 rounded bg-light-secondary border">
                                              <p className="mb-0">No prescriptions for this date.</p>
                                            </div>
                                          )}
                                        </>
                                      )}
                                      {activeFilters.includes("GAPLD") && (
                                        <>
                                          {appointment.GAPLD && appointment.GAPLD.length > 0 ? (
                                            <div className="mb-4">
                                              <h5 className="fw-bold">GAPLD</h5>
                                              <div
                                                style={{
                                                  padding: "16px",
                                                  borderRadius: "8px",
                                                  backgroundColor: "#d4edda",
                                                  border: "1px solid #c3e6cb",
                                                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                  display: "block",
                                                  minHeight: "50px",
                                                }}
                                              >
                                                {renderGAPLDContent(appointment.GAPLD)}
                                              </div>
                                            </div>
                                          ) : (
                                            <div
                                              style={{
                                                padding: "16px",
                                                borderRadius: "8px",
                                                backgroundColor: "#f8f9fa",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              <p className="mb-0">No GAPLD records for this date.</p>
                                            </div>
                                          )}
                                        </>
                                      )}
                                      {activeFilters.includes("Menopause") && (
                                        <>
                                          {appointment.menopause && appointment.menopause.length > 0 ? (
                                            <div className="mb-4">
                                              <h5 className="fw-bold">Menopause</h5>
                                              <div className="p-3 rounded bg-light-info border shadow-sm">
                                                {renderMenopauseContent(appointment.menopause)}
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="p-3 rounded bg-light-secondary border">
                                              <p className="mb-0">No menopause records for this date.</p>
                                            </div>
                                          )}
                                        </>
                                      )}
                                      {activeFilters.includes("male_chart") && (
                                        <>
                                          {appointment.male_chart && appointment.male_chart.length > 0 ? (
                                            <div className="mb-4">
                                              <h5 className="fw-bold">Male Chart</h5>
                                              <div className="p-3 rounded bg-light-warning border shadow-sm">
                                                {renderMaleChartContent(appointment.male_chart)}
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="p-3 rounded bg-light-secondary border">
                                              <p className="mb-0">No male chart records for this date.</p>
                                            </div>
                                          )}
                                        </>
                                      )}
                                      {activeFilters.includes("Counsellor") && (
                                        <>
                                          {appointment.counsellor && Object.keys(appointment.counsellor).length > 0 ? (
                                            <div className="mb-4">
                                              <h5 className="fw-bold">Counsellor Session</h5>
                                              <div className="p-3 rounded bg-light-warning border shadow-sm">
                                                <p>
                                                  <strong>Session Date:</strong>{" "}
                                                  {formatDate(appointment.counsellor.consulting_date) || "Not specified"}
                                                </p>
                                                <p>
                                                  <strong>Medicine Explained Date:</strong>{" "}
                                                  {formatDate(appointment.counsellor.medicine_explained_date) ||
                                                    "Not specified"}
                                                </p>
                                                <p>
                                                  <strong>Medicine Explained By:</strong>{" "}
                                                  {appointment.counsellor.medicine_explined_by || "Not specified"}
                                                </p>
                                                {appointment.counsellor.recommendations &&
                                                  appointment.counsellor.recommendations.length > 0 && (
                                                    <div>
                                                      <strong>Recommendations:</strong>
                                                      <ul>
                                                        {appointment.counsellor.recommendations.map((rec, recIndex) => (
                                                          <li key={recIndex}>{rec}</li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="p-3 rounded bg-light-secondary border">
                                              <p className="mb-0">No counsellor data for this date.</p>
                                            </div>
                                          )}
                                        </>
                                      )}
                                      {activeFilters.includes("Medical") && (
                                        <>
                                          {appointment.medical &&
                                            appointment.medical.length > 0 &&
                                            appointment.medical.map((medical, medIndex) => (
                                              <div key={medIndex} className="mb-4">
                                                <h5 className="fw-bold">Medical Record</h5>
                                                <div className="p-3 rounded bg-light-info border shadow-sm">
                                                  <p>
                                                    <strong>Medicine Prepared By:</strong>{" "}
                                                    {formatDate(medical.medicine_prepared_by) || "Not specified"}
                                                  </p>
                                                  <p>
                                                    <strong>Medicine Send Date:</strong>{" "}
                                                    {formatDate(medical.medicne_send_date) || "Not specified"}
                                                  </p>
                                                  <p>
                                                    <strong>Delivery Date:</strong>{" "}
                                                    {formatDate(medical.delivery_date) || "Not specified"}
                                                  </p>
                                                  {medical.Medicines && medical.Medicines.length > 0 && (
                                                    <div>
                                                      <strong>Medicines:</strong>
                                                      <ul>
                                                        {medical.Medicines.map((medicine, medIdx) => (
                                                          <li key={medIdx}>
                                                            {medicine.medicine_name} - {medicine.delivery_status}
                                                          </li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                        </>
                                      )}
                                      {activeFilters.includes("Followup") && (
                                        <>
                                          {appointment.followup_calls && appointment.followup_calls.length > 0 ? (
                                            appointment.followup_calls.map((followup, fIndex) => (
                                              <div key={fIndex} className="mb-4">
                                                <h5 className="fw-bold" style={{ marginTop: "1rem" }}>Follow-up Call</h5>
                                                <div className="p-3 rounded bg-light-success border shadow-sm">
                                                  <h6>Follow-up ID: {followup.followup_id}</h6>
                                                  {followup.questionnaire && followup.questionnaire.length > 0 && (
                                                    <div>
                                                      <strong>Questionnaire:</strong>
                                                      {followup.questionnaire.map((q, qIdx) => (
                                                        <div key={qIdx} className="mt-3">
                                                          {q.isGroup === "Yes" &&
                                                            q.questions &&
                                                            q.questions.length > 0 ? (
                                                            <div className="card border-primary mb-3">
                                                              <div className="card-header bg-light-primary">
                                                                <h6 className="fw-bold text-primary mb-0 fs-6">
                                                                  {q.group_name || `Group ${q.group_id}`}
                                                                </h6>
                                                              </div>
                                                              <div className="card-body p-2 p-md-3">
                                                                {q.questions.map((groupQuestion, gqIdx) => (
                                                                  <div key={gqIdx} className="mb-3">
                                                                    <p className="mb-1 text-break">
                                                                      <strong>{groupQuestion.question}</strong>
                                                                    </p>
                                                                    {groupQuestion.question_type === "File Input" && groupQuestion.answer ? (
                                                                      <div className="mb-1">
                                                                        <a 
                                                                          href={groupQuestion.answer} 
                                                                          target="_blank" 
                                                                          rel="noopener noreferrer"
                                                                          className="btn btn-sm btn-outline-primary"
                                                                          style={{ textDecoration: 'none' }}
                                                                        >
                                                                          📎 View {groupQuestion.short_form || 'File'}
                                                                        </a>
                                                                      </div>
                                                                    ) : (
                                                                      <p className="mb-1 text-break">
                                                                        Answer: {groupQuestion.answer}
                                                                      </p>
                                                                    )}
                                                                    {groupQuestion.is_important === "Yes" && (
                                                                      <span className="badge bg-warning">Important</span>
                                                                    )}
                                                                    {groupQuestion.note_description && (
                                                                      <p className="text-muted mt-1 text-break">
                                                                        <small>
                                                                          Note: {groupQuestion.note_description}
                                                                        </small>
                                                                      </p>
                                                                    )}
                                                                  </div>
                                                                ))}
                                                              </div>
                                                            </div>
                                                          ) : (
                                                            <div>
                                                              <p className="mb-1 text-break">
                                                                <strong>{q.question}</strong>
                                                              </p>
                                                              {q.question_type === "File Input" && q.answer ? (
                                                                <div className="mb-1">
                                                                  <a 
                                                                    href={q.answer} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    style={{ textDecoration: 'none' }}
                                                                  >
                                                                    📎 View {q.short_form || 'File'}
                                                                  </a>
                                                                </div>
                                                              ) : (
                                                                <p className="mb-1 text-break">Answer: {q.answer}</p>
                                                              )}
                                                              {q.is_important === "Yes" && (
                                                                <span className="badge bg-warning">Important</span>
                                                              )}
                                                              {q.note_description && (
                                                                <p className="text-muted mt-1 text-break">
                                                                  <small>Note: {q.note_description}</small>
                                                                </p>
                                                              )}
                                                            </div>
                                                          )}
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                  {followup.notes && followup.notes.length > 0 && (
                                                    <div>
                                                      <strong>Notes:</strong>
                                                      {followup.notes.map((note, nIdx) => (
                                                        <div key={nIdx} className="mt-2">
                                                          <p>{note.note_description}</p>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            ))
                                          ) : (
                                            <div className="p-3 rounded bg-light-secondary border">
                                              <p className="mb-0">No followup data for this date.</p>
                                            </div>
                                          )}
                                        </>
                                      )}
                                      {activeFilters.includes("Report") && (
                                        <>
                                          {appointment.reports.length > 0 ? (
                                            <div className="row">
                                              {appointment.reports.map((report, reportIndex) => {
                                                // Determine which files to display (new format: reportFiles array, or old format: fileUrl)
                                                const filesToDisplay = (report.reportFiles && report.reportFiles.length > 0)
                                                  ? report.reportFiles
                                                    .filter(f => f && (f.fileUrl || f.report_file_url) && (f.fileUrl || f.report_file_url).trim() !== "")
                                                    .map(f => f.fileUrl || f.report_file_url)
                                                  : (report.fileUrl && report.fileUrl.trim() !== "" ? [report.fileUrl] : [])
                                                
                                                // Get current slide for this report from state object
                                                const currentSlide = reportSlides[reportIndex] || 0
                                                const setCurrentSlide = (newSlide) => {
                                                  if (typeof newSlide === 'function') {
                                                    setReportSlides(prev => ({
                                                      ...prev,
                                                      [reportIndex]: newSlide(prev[reportIndex] || 0)
                                                    }))
                                                  } else {
                                                    setReportSlides(prev => ({
                                                      ...prev,
                                                      [reportIndex]: newSlide
                                                    }))
                                                  }
                                                }

                                                return (filesToDisplay.length > 0) ? (
                                                  <div key={reportIndex} className="col-12 col-md-6 mb-4">
                                                    <div className="p-3 rounded bg-light-info border shadow-sm h-100 d-flex flex-column">
                                                      {/* Report Info */}
                                                      <div className="mb-2">
                                                        <p className="mb-1"><strong>{report.title || "Report"}</strong></p>
                                                        {report.description && <p className="mb-1 text-muted small">{report.description}</p>}
                                                        <p className="mb-2 text-muted small">Date: {report.date || "-"}</p>
                                                      </div>

                                                      {/* Carousel */}
                                                      <div style={{ position: "relative", backgroundColor: "#f5f5f5", borderRadius: "4px", overflow: "hidden" }}>
                                                        {/* Main Image */}
                                                        <img
                                                          src={filesToDisplay[currentSlide] || ""}
                                                          alt={`Report ${reportIndex + 1} - Slide ${currentSlide + 1}`}
                                                          className="img-fluid"
                                                          style={{
                                                            width: "100%",
                                                            height: "300px",
                                                            objectFit: "cover",
                                                            display: "block",
                                                          }}
                                                          onError={(e) => {
                                                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100%" height="300"%3E%3Crect fill="%23f0f0f0" width="100%" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="%23999"%3EImage Not Available%3C/text%3E%3C/svg%3E'
                                                          }}
                                                        />

                                                        {/* Left Arrow */}
                                                        {filesToDisplay.length > 1 && (
                                                          <button
                                                            onClick={() => setCurrentSlide((prev) => (prev === 0 ? filesToDisplay.length - 1 : prev - 1))}
                                                            style={{
                                                              position: "absolute",
                                                              left: "10px",
                                                              top: "50%",
                                                              transform: "translateY(-50%)",
                                                              backgroundColor: "rgba(0, 0, 0, 0.5)",
                                                              color: "white",
                                                              border: "none",
                                                              borderRadius: "50%",
                                                              width: "40px",
                                                              height: "40px",
                                                              fontSize: "20px",
                                                              cursor: "pointer",
                                                              display: "flex",
                                                              alignItems: "center",
                                                              justifyContent: "center",
                                                              transition: "background-color 0.2s",
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(0, 0, 0, 0.8)"}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = "rgba(0, 0, 0, 0.5)"}
                                                            title="Previous"
                                                          >
                                                            ❮
                                                          </button>
                                                        )}

                                                        {/* Right Arrow */}
                                                        {filesToDisplay.length > 1 && (
                                                          <button
                                                            onClick={() => setCurrentSlide((prev) => (prev === filesToDisplay.length - 1 ? 0 : prev + 1))}
                                                            style={{
                                                              position: "absolute",
                                                              right: "10px",
                                                              top: "50%",
                                                              transform: "translateY(-50%)",
                                                              backgroundColor: "rgba(0, 0, 0, 0.5)",
                                                              color: "white",
                                                              border: "none",
                                                              borderRadius: "50%",
                                                              width: "40px",
                                                              height: "40px",
                                                              fontSize: "20px",
                                                              cursor: "pointer",
                                                              display: "flex",
                                                              alignItems: "center",
                                                              justifyContent: "center",
                                                              transition: "background-color 0.2s",
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(0, 0, 0, 0.8)"}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = "rgba(0, 0, 0, 0.5)"}
                                                            title="Next"
                                                          >
                                                            ❯
                                                          </button>
                                                        )}
                                                      </div>

                                                      {/* Dots Indicator */}
                                                      {filesToDisplay.length > 1 && (
                                                        <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "12px" }}>
                                                          {filesToDisplay.map((_, dotIndex) => (
                                                            <button
                                                              key={dotIndex}
                                                              onClick={() => setCurrentSlide(dotIndex)}
                                                              style={{
                                                                width: "10px",
                                                                height: "10px",
                                                                borderRadius: "50%",
                                                                backgroundColor: currentSlide === dotIndex ? "#007bff" : "#ccc",
                                                                border: "none",
                                                                cursor: "pointer",
                                                                transition: "background-color 0.2s",
                                                              }}
                                                              title={`Go to slide ${dotIndex + 1}`}
                                                            />
                                                          ))}
                                                        </div>
                                                      )}

                                                      {/* Slide Counter */}
                                                      {filesToDisplay.length > 1 && (
                                                        <p style={{ textAlign: "center", marginTop: "8px", fontSize: "12px", color: "#666" }}>
                                                          {currentSlide + 1} of {filesToDisplay.length}
                                                        </p>
                                                      )}
                                                    </div>
                                                  </div>
                                                ) : null
                                              })}
                                            </div>
                                          ) : (
                                            <div className="p-3 rounded bg-light-secondary border">
                                              <p className="mb-0">No reports for this date.</p>
                                            </div>
                                          )}
                                        </>
                                      )}
                                      {activeFilters.includes("Notes") && (
                                        <>
                                          {appointment.notes.map((note, noteIndex) => (
                                            <div key={noteIndex} className="mb-4">
                                              <h5 className="fw-bold d-flex align-items-center fs-6">
                                                {"Note"}
                                                <span className="badge bg-primary rounded-pill fs-6 ms-2">
                                                  {note.index}
                                                </span>
                                              </h5>
                                              <div className="p-2 p-md-3 rounded bg-light-primary border shadow-sm">
                                                <p className="mb-0 text-dark text-break">{note.note_description}</p>
                                                <div className="mt-2 d-flex flex-wrap gap-1">
                                                  {note.is_important === "Yes" && (
                                                    <span className="badge bg-warning">Important</span>
                                                  )}
                                                  {note.is_emergency === "Yes" && (
                                                    <span className="badge bg-danger">Emergency</span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </>
                                      )}
                                      {activeFilters.includes("DoctorNotes") && (
                                        <>
                                          {appointment.notes
                                            .filter(note => note.note_type === "Doctor")
                                            .map((note, noteIndex) => (
                                              <div key={noteIndex} className="mb-4">
                                                <h5 className="fw-bold d-flex align-items-center fs-6">
                                                  {"Doctor Note"}
                                                  <span className="badge bg-success rounded-pill fs-6 ms-2">
                                                    {note.index}
                                                  </span>
                                                </h5>
                                                <div className="p-2 p-md-3 rounded bg-light-success border shadow-sm">
                                                  <p className="mb-0 text-dark text-break">{note.note_description}</p>
                                                  <div className="mt-2 d-flex flex-wrap gap-1">
                                                    {note.is_important === "Yes" && (
                                                      <span className="badge bg-warning">Important</span>
                                                    )}
                                                    {note.is_emergency === "Yes" && (
                                                      <span className="badge bg-danger">Emergency</span>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                        </>
                                      )}
                                    </div>
                                  </Accordion.Collapse>
                                </Accordion.Item>
                              )
                            })
                          ) : (
                            <div className="text-center py-5">
                              <div className="mb-3">
                                <i className="fa fa-inbox text-muted" style={{ fontSize: "48px" }}></i>
                              </div>
                              <h5 className="text-muted mb-2">No Data Available</h5>
                              <p className="text-muted mb-0">
                                {activeFilters.length === 0
                                  ? "No records found for this patient."
                                  : `No records found matching the selected filters.`}
                              </p>
                            </div>
                          )}
                        </Accordion>
                      </div>
                    </div>
                  </div>
                </Col>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {imagePreview.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={closeImagePreview}
        >
          <div
            style={{
              position: 'relative',
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeImagePreview}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Close preview"
            >
              ✕
            </button>

            {/* Main Image */}
            <img
              src={imagePreview.images[imagePreview.currentIndex]}
              alt={`Preview ${imagePreview.currentIndex + 1}`}
              style={{
                maxWidth: '80vw',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: '4px',
              }}
            />

            {/* Image Counter */}
            <p style={{ margin: '15px 0', fontSize: '14px', color: '#666' }}>
              {imagePreview.currentIndex + 1} of {imagePreview.images.length}
            </p>

            {/* Navigation Arrows */}
            {imagePreview.images.length > 1 && (
              <div
                style={{
                  display: 'flex',
                  gap: '15px',
                  marginTop: '10px',
                }}
              >
                <button
                  onClick={goToPreviousImage}
                  disabled={imagePreview.currentIndex === 0}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: imagePreview.currentIndex === 0 ? '#ccc' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: imagePreview.currentIndex === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  ← Previous
                </button>
                <button
                  onClick={goToNextImage}
                  disabled={imagePreview.currentIndex === imagePreview.images.length - 1}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: imagePreview.currentIndex === imagePreview.images.length - 1 ? '#ccc' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: imagePreview.currentIndex === imagePreview.images.length - 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default CasePaper
